import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Trash2, Printer, History, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LabelHistory() {
  const navigate = useNavigate();
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const res = await axios.get(`${API}/labels?limit=100`);
        setLabels(res.data);
      } catch {
        toast.error("Failed to load labels");
      } finally {
        setLoading(false);
      }
    };
    fetchLabels();
  }, []);

  const handleDelete = async (label) => {
    try {
      await axios.delete(`${API}/labels/${label.id}`);
      setLabels((prev) => prev.filter((l) => l.id !== label.id));
      toast.success("Label deleted");
    } catch {
      toast.error("Failed to delete label");
    }
  };

  const handleReprint = (label) => {
    navigate(`/create?reprint=${label.id}`);
  };

  return (
    <div data-testid="label-history-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1
          className="text-4xl md:text-5xl font-bold tracking-tight uppercase"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          LABEL HISTORY
        </h1>
        <p className="text-sm text-muted-foreground tracking-wider uppercase mt-1">
          ALL PREVIOUSLY PRINTED LABELS
        </p>
      </div>

      {/* Table - Desktop */}
      <Card className="bg-card border border-border/50 rounded-sm overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border/50 hover:bg-transparent">
              <TableHead className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground py-4 px-5">
                PRODUCT
              </TableHead>
              <TableHead className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground py-4">
                WEIGHT
              </TableHead>
              <TableHead className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground py-4">
                BATCH #
              </TableHead>
              <TableHead className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground py-4">
                PACK DATE
              </TableHead>
              <TableHead className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground py-4">
                EXPIRATION
              </TableHead>
              <TableHead className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground py-4">
                CREATED
              </TableHead>
              <TableHead className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground py-4 text-right pr-5">
                ACTIONS
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : labels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <History className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground text-sm">No label history yet</p>
                  <Button
                    data-testid="create-label-from-history-btn"
                    onClick={() => navigate("/create")}
                    className="mt-4 bg-primary text-primary-foreground rounded-sm"
                  >
                    CREATE YOUR FIRST LABEL
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              labels.map((label) => (
                <TableRow
                  key={label.id}
                  data-testid={`history-row-${label.id}`}
                  className="border-border/30 hover:bg-secondary/30 transition-colors"
                >
                  <TableCell className="py-4 px-5">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-sm flex items-center justify-center shrink-0">
                        <Package className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span className="font-bold text-sm uppercase tracking-wide">
                        {label.product_name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <span className="font-mono text-sm">
                      {label.weight} {label.weight_unit}
                    </span>
                  </TableCell>
                  <TableCell className="py-4">
                    <span className="font-mono text-sm tracking-wider">
                      {label.batch_number}
                    </span>
                  </TableCell>
                  <TableCell className="py-4">
                    <span className="font-mono text-sm">
                      {label.pack_date || "--"}
                    </span>
                  </TableCell>
                  <TableCell className="py-4">
                    <span className="font-mono text-sm">
                      {label.expiration_date}
                    </span>
                  </TableCell>
                  <TableCell className="py-4">
                    <span className="text-xs text-muted-foreground font-mono">
                      {label.created_at ? format(new Date(label.created_at), "dd/MM/yyyy HH:mm") : "--"}
                    </span>
                  </TableCell>
                  <TableCell className="py-4 pr-5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        data-testid={`reprint-label-${label.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => handleReprint(label)}
                        className="h-10 w-10 hover:text-primary"
                        title="Reprint"
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`delete-label-${label.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(label)}
                        className="h-10 w-10 hover:text-destructive"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </Card>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : labels.length === 0 ? (
          <div className="text-center py-12">
            <History className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground text-sm">No label history yet</p>
            <Button
              onClick={() => navigate("/create")}
              className="mt-4 bg-primary text-primary-foreground rounded-sm"
            >
              CREATE YOUR FIRST LABEL
            </Button>
          </div>
        ) : (
          labels.map((label) => (
            <Card
              key={label.id}
              data-testid={`mobile-history-${label.id}`}
              className="bg-card border border-border/50 rounded-sm p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center shrink-0">
                    <Package className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-bold text-base uppercase tracking-wide">{label.product_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{label.weight} {label.weight_unit}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => handleReprint(label)} className="h-9 w-9 hover:text-primary">
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(label)} className="h-9 w-9 hover:text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                <div><span className="text-muted-foreground">BATCH:</span> {label.batch_number}</div>
                <div><span className="text-muted-foreground">PKD:</span> {label.pack_date || "--"}</div>
                <div><span className="text-muted-foreground">EXP:</span> {label.expiration_date}</div>
                <div><span className="text-muted-foreground">{label.created_at ? format(new Date(label.created_at), "dd/MM HH:mm") : "--"}</span></div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
